import type { ThemeType } from '@/config/objects/theme-types';
import {
  Breadcrumbs,
  Button,
  ConfirmDialog,
  DetailPageHeader,
  DetailPageHero,
  PageContainer,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Palette, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from '@startupquickstart/frontend';

const tabs = [
  { label: 'Overview', path: 'overview', icon: Palette },
  { label: 'Themes', path: 'themes', icon: Palette },
];

export function ThemeTypeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data, isLoading } = useQuery<{ success: boolean; data: ThemeType }>({
    queryKey: ['ThemeType', id],
    queryFn: () => apiClient.get(`/theme-types/${id}`),
    enabled: !!id,
  });

  const themeType = data?.data;

  const deleteMutation = useMutation({
    mutationFn: async (themeTypeId: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/theme-types/${themeTypeId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['ThemeType'] });
        navigate('/theme-types');
      } else {
        throw new Error(response.error || 'Failed to delete theme type');
      }
    },
  });

  const handleDelete = () => {
    if (!themeType) return;
    toast.promise(deleteMutation.mutateAsync(themeType.id), {
      loading: 'Deleting theme type...',
      success: 'Theme type deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete theme type',
    });
  };

  useEffect(() => {
    if (id) {
      const basePath = `/theme-types/${id}`;
      if (location.pathname === basePath || location.pathname === `${basePath}/`) {
        navigate(`${basePath}/overview`, { replace: true });
      }
    }
  }, [id, location.pathname, navigate]);

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Theme Types', href: '/theme-types' },
    { label: themeType?.name || 'Theme Type', current: true },
  ];

  const basePath = `/theme-types/${id}`;

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-lg font-medium mb-2">Theme Type Not Found</h2>
          <p className="text-muted-foreground">
            The requested theme type could not be found.
          </p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer padding="none" className="flex flex-col overflow-hidden">
      <DetailPageHeader
        actions={
          themeType && (
            <>
              <Button variant="outline" onClick={() => navigate(`${basePath}/edit`)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          )
        }
      >
        <Breadcrumbs items={breadcrumbs} />
      </DetailPageHeader>

      <DetailPageHero
        isLoading={isLoading}
        category={{ label: 'Theme Type', icon: Palette }}
        title={themeType?.name || 'Theme Type'}
        image={{
          url: null,
          alt: themeType?.name || 'Theme Type',
          fallbackIcon: Palette,
        }}
        meta={themeType?.slug && (
              <span className="text-sm text-white/60 font-mono">{themeType.slug}</span>
            )}
        tabs={tabs}
        basePath={basePath}
      >
        <Outlet context={{ themeType, isLoading }} />
      </DetailPageHero>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Theme Type"
        description={`Are you sure you want to delete "${themeType?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </PageContainer>
  );
}
